import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import {
    FaClock, FaCheckCircle, FaTimesCircle, FaFire,
    FaHistory, FaChartBar, FaUserShield, FaBell
} from 'react-icons/fa';
import { toast, Toaster } from 'react-hot-toast';

/* ── Bildirim yöneticisi ─────────────────────────────────────────────────── */
let _nId = 0;
const NotifManager = (() => {
    let listeners = [];
    return {
        subscribe: (fn) => { listeners.push(fn); return () => { listeners = listeners.filter(l => l !== fn); }; },
        emit: (n)  => listeners.forEach(l => l({ ...n, id: ++_nId, ts: Date.now() })),
    };
})();

/* ── Bildirim overlay ────────────────────────────────────────────────────── */
const DashNotifOverlay = () => {
    const [notifler, setNotifler] = useState([]);
    const [goruldu,  setGoruldu]  = useState(false);
    const timerRef    = useRef({});
    const gorulduRef  = useRef(false);

    useEffect(() => {
        const aktiflestir = () => {
            if (gorulduRef.current) return;
            gorulduRef.current = true;
            setGoruldu(true);
        };
        window.addEventListener('mousemove',       aktiflestir, { once: true });
        window.addEventListener('keydown',         aktiflestir, { once: true });
        window.addEventListener('click',           aktiflestir, { once: true });
        window.addEventListener('scroll',          aktiflestir, { once: true });
        const visChange = () => { if (!document.hidden) aktiflestir(); };
        document.addEventListener('visibilitychange', visChange);
        return () => document.removeEventListener('visibilitychange', visChange);
    }, []);

    useEffect(() => {
        return NotifManager.subscribe(n => {
            setNotifler(prev => [...prev.slice(-6), n]);
        });
    }, []);

    useEffect(() => {
        if (!goruldu) return;
        notifler.forEach((n, i) => {
            if (timerRef.current[n.id]) return;
            timerRef.current[n.id] = setTimeout(() => {
                setNotifler(prev => prev.filter(x => x.id !== n.id));
                delete timerRef.current[n.id];
            }, 3200 + i * 500);
        });
    }, [goruldu, notifler.length]);

    if (!notifler.length) return null;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            display: 'flex', flexDirection: 'column', pointerEvents: 'none',
        }}>
            {notifler.map((n, i) => (
                <div key={n.id} style={{
                    position: 'relative',
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: `linear-gradient(90deg, rgba(8,9,12,.97) 0%, ${n.renk}22 40%, ${n.renk}22 60%, rgba(8,9,12,.97) 100%)`,
                    borderBottom: `1px solid ${n.renk}60`,
                    padding: '11px 28px',
                    backdropFilter: 'blur(12px)',
                    animation: 'notifSlide .3s cubic-bezier(.22,1,.36,1) both',
                }}>
                    {/* İkon */}
                    <div style={{
                        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                        background: `${n.renk}20`, border: `1px solid ${n.renk}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: n.renk, fontSize: '.85rem',
                    }}>{n.icon}</div>

                    {/* Metin */}
                    <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '.82rem', fontWeight: 700, color: '#f1f5f9' }}>
                            {n.username}
                        </span>
                        <span style={{ fontSize: '.8rem', color: 'rgba(255,255,255,.5)', marginLeft: 8 }}>
                            {n.mesaj}
                        </span>
                        <span style={{ fontSize: '.72rem', color: n.renk, marginLeft: 8, fontWeight: 600 }}>
                            {n.bonus}
                        </span>
                    </div>

                    {/* Saat */}
                    <span style={{ fontSize: '.68rem', color: 'rgba(255,255,255,.2)', flexShrink: 0 }}>{n.saat}</span>

                    {/* Durum badge */}
                    <span style={{
                        padding: '3px 10px', borderRadius: 6, flexShrink: 0,
                        fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px',
                        background: `${n.renk}18`, color: n.renk, border: `1px solid ${n.renk}35`,
                    }}>{n.durum}</span>

                    {/* Progress bar */}
                    <div style={{
                        position: 'absolute', bottom: 0, left: 0,
                        height: 2, background: n.renk,
                        boxShadow: `0 0 8px ${n.renk}`,
                        opacity: .9,
                        ...(goruldu
                            ? { animation: `notifProg ${3200 + i * 500}ms linear both` }
                            : { right: 0 }
                        ),
                    }} />

                    {/* Görülmedi uyarı */}
                    {!goruldu && (
                        <span style={{
                            fontSize: '.58rem', color: 'rgba(255,255,255,.2)',
                            textTransform: 'uppercase', letterSpacing: '1px',
                            animation: 'bekliyorPulse 1.5s infinite',
                        }}>● bekliyor</span>
                    )}
                </div>
            ))}
        </div>
    );
};

/* ── Dashboard ───────────────────────────────────────────────────────────── */
const Dashboard = () => {
    const [requests, setRequests]     = useState([]);
    const [bonusMap, setBonusMap]     = useState({});
    const [loading,  setLoading]      = useState(true);
    const [isSystemActive, setIsSystemActive] = useState(true);
    const [loadingStatus,  setLoadingStatus]  = useState(true);
    const [flashRows, setFlashRows]   = useState({});  // id → true

    const oncekiRef = useRef({});  // id → status snapshot

    const fetchSystemStatus = () => {
        axiosInstance.get('/siteSettings')
            .then(r => setIsSystemActive(r.data.bonusRequestsEnabled))
            .catch(e => console.error(e))
            .finally(() => setLoadingStatus(false));
    };

    const fetchDashboardData = async () => {
        try {
            const [pendingRes, approvedRes, rejectedRes, bonusesRes] = await Promise.all([
                axiosInstance.get('/request'),
                axiosInstance.get('/request/approved'),
                axiosInstance.get('/request/rejected'),
                axiosInstance.get('/bonus/all'),
            ]);
            const bMap = {};
            (bonusesRes.data || []).forEach(b => { bMap[b.bonusType || b._id] = b.name; });
            setBonusMap(bMap);

            const all = [
                ...(pendingRes.data  || []),
                ...(approvedRes.data || []),
                ...(rejectedRes.data || []),
            ];

            /* ── Diff: önceki state ile karşılaştır ── */
            const onceki = oncekiRef.current;
            const ilkYukleme = Object.keys(onceki).length === 0;
            const yeniFlash = {};

            all.forEach(req => {
                const eskiStatus = onceki[req._id];
                const yeniStatus = req.status;

                // İlk yüklemede sadece snapshot al, bildirim gönderme
                if (ilkYukleme) return;

                // Daha önce hiç görülmemiş talep (yeni eklendi) → direkt bildir
                const statusDegisti = eskiStatus && eskiStatus !== yeniStatus;
                const yeniEklendi   = !eskiStatus && (yeniStatus === 'approved' || yeniStatus === 'rejected' || yeniStatus === 'pending');

                if (statusDegisti || yeniEklendi) {
                    yeniFlash[req._id] = true;

                    const saat = new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                    const bonus = bMap[req.bonusType] || req.bonusType || '';

                    if (yeniStatus === 'approved') {
                        NotifManager.emit({
                            username: req.username,
                            mesaj: yeniEklendi ? 'onaylı talep geldi →' : 'talebi onaylandı →',
                            bonus, durum: 'Onaylandı', renk: '#10b981',
                            icon: <FaCheckCircle />, saat,
                        });
                    } else if (yeniStatus === 'rejected') {
                        NotifManager.emit({
                            username: req.username,
                            mesaj: yeniEklendi ? 'reddedilmiş talep geldi →' : 'talebi reddedildi →',
                            bonus, durum: 'Reddedildi', renk: '#ef4444',
                            icon: <FaTimesCircle />, saat,
                        });
                    } else if (yeniStatus === 'pending') {
                        NotifManager.emit({
                            username: req.username,
                            mesaj: 'yeni talep oluşturdu →',
                            bonus, durum: 'Yeni Talep', renk: '#3b82f6',
                            icon: <FaClock />, saat,
                        });
                    }
                }
            });

            // Snapshot güncelle
            oncekiRef.current = Object.fromEntries(all.map(r => [r._id, r.status]));

            // Flash efekti
            if (Object.keys(yeniFlash).length) {
                setFlashRows(yeniFlash);
                setTimeout(() => setFlashRows({}), 1800);
            }

            setRequests(all);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        fetchDashboardData();
        fetchSystemStatus();
        const i1 = setInterval(fetchDashboardData, 30000);
        const i2 = setInterval(fetchSystemStatus,  20000);
        return () => { clearInterval(i1); clearInterval(i2); };
    }, []);

    const handleCopy = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        toast.success('Kopyalandı!', { style: { background: '#1e2024', color: '#fff' } });
    };

    const stats = {
        pending:  requests.filter(r => r.status === 'pending').length,
        approved: requests.filter(r => r.status === 'approved').length,
        rejected: requests.filter(r => r.status === 'rejected').length,
    };

    const assignmentData = requests.reduce((acc, curr) => {
        if (curr.assignedTo) acc[curr.assignedTo] = (acc[curr.assignedTo] || 0) + 1;
        return acc;
    }, {});
    const sortedAssignments = Object.entries(assignmentData)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    const totalAssigned = sortedAssignments.reduce((s, i) => s + i.count, 0);

    const popularBonusCode = requests.length > 0
        ? Object.entries(requests.reduce((acc, curr) => {
            acc[curr.bonusType] = (acc[curr.bonusType] || 0) + 1; return acc;
          }, {})).sort((a, b) => b[1] - a[1])[0]?.[0]
        : null;
    const popularBonusName = bonusMap[popularBonusCode] || popularBonusCode || 'Veri Yok';

    const statCards = [
        { label: 'Bekleyen',   val: stats.pending,  icon: <FaClock />,       accent: '#3b82f6', bg: 'rgba(59,130,246,.08)',  border: 'rgba(59,130,246,.18)' },
        { label: 'Onaylanan',  val: stats.approved, icon: <FaCheckCircle />, accent: '#10b981', bg: 'rgba(16,185,129,.08)',  border: 'rgba(16,185,129,.18)' },
        { label: 'Reddedilen', val: stats.rejected, icon: <FaTimesCircle />, accent: '#ef4444', bg: 'rgba(239,68,68,.08)',   border: 'rgba(239,68,68,.18)'  },
        { label: 'En Popüler', val: popularBonusName, icon: <FaFire />,      accent: '#f59e0b', bg: 'rgba(245,158,11,.08)',  border: 'rgba(245,158,11,.18)', small: true },
    ];

    const recent = [...requests]
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .slice(0, 8);

    return (
        <div style={{
            minHeight: '100vh', background: '#090a0c',
            padding: '28px 28px 40px', color: '#e2e8f0',
            fontFamily: "'Outfit','Inter',sans-serif", boxSizing: 'border-box',
        }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
                * { box-sizing: border-box; }

                /* Bildirim animasyonları */
                @keyframes notifSlide {
                    from { opacity:0; transform: translateY(-100%); }
                    to   { opacity:1; transform: translateY(0); }
                }
                @keyframes notifProg {
                    from { right: 0; }
                    to   { right: 100%; }
                }
                @keyframes bekliyorPulse {
                    0%,100% { opacity:.5; } 50% { opacity:1; }
                }

                /* Dashboard animasyonları */
                @keyframes fadeUp {
                    from { opacity:0; transform: translateY(12px); }
                    to   { opacity:1; transform: none; }
                }
                .anim-1 { animation: fadeUp .35s ease .05s both; }
                .anim-2 { animation: fadeUp .35s ease .12s both; }
                .anim-3 { animation: fadeUp .35s ease .19s both; }
                .anim-4 { animation: fadeUp .35s ease .26s both; }

                /* Flash glow — değişen satır */
                @keyframes rowFlash {
                    0%   { box-shadow: 0 0 0 2px rgba(255,255,255,.35); }
                    50%  { box-shadow: 0 0 16px 4px rgba(255,255,255,.2); }
                    100% { box-shadow: none; }
                }
                .flash-row { animation: rowFlash 1.8s ease both; }

                .dash-stat:hover { transform: translateY(-3px) !important; box-shadow: 0 16px 40px rgba(0,0,0,.5) !important; }
                .dash-row:hover  { background: rgba(255,255,255,.035) !important; border-color: rgba(255,255,255,.07) !important; transform: translateX(3px); }
                .dash-op:hover   { background: rgba(255,255,255,.03) !important; }

                @keyframes pulseDot {
                    0%,100% { opacity:1; transform:scale(1); }
                    50%     { opacity:.4; transform:scale(.7); }
                }
            `}</style>

            <DashNotifOverlay />
            <Toaster position="top-right" />

            {/* ── Header ───────────────────────────────────────── */}
            <div className="anim-1" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                        width: 44, height: 44, borderRadius: 13,
                        background: 'linear-gradient(135deg,rgba(242,113,39,.25),rgba(242,113,39,.06))',
                        border: '1px solid rgba(242,113,39,.25)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#f27127', fontSize: '1.15rem',
                        boxShadow: '0 0 20px rgba(242,113,39,.12)',
                    }}><FaUserShield /></div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-.3px' }}>Dashboard</h2>
                        <p style={{ margin: 0, fontSize: '.72rem', color: 'rgba(255,255,255,.25)', marginTop: 2 }}>
                            Genel Bakış · Canlı
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Bildirim istatistiği */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '7px 14px', borderRadius: 40,
                        background: 'rgba(255,255,255,.04)',
                        border: '1px solid rgba(255,255,255,.07)',
                        fontSize: '.75rem', color: 'rgba(255,255,255,.3)',
                    }}>
                        <FaBell style={{ fontSize: '.75rem' }} />
                        <span>30sn'de yenileniyor</span>
                    </div>

                    {/* Sistem durumu */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 16px', borderRadius: 40,
                        background: isSystemActive ? 'rgba(16,185,129,.08)' : 'rgba(239,68,68,.08)',
                        border: `1px solid ${isSystemActive ? 'rgba(16,185,129,.2)' : 'rgba(239,68,68,.2)'}`,
                        fontSize: '.75rem', fontWeight: 600,
                        color: isSystemActive ? '#10b981' : '#ef4444',
                    }}>
                        <span style={{
                            width: 7, height: 7, borderRadius: '50%',
                            background: isSystemActive ? '#10b981' : '#ef4444',
                            boxShadow: `0 0 8px ${isSystemActive ? '#10b981' : '#ef4444'}`,
                            display: 'inline-block', animation: 'pulseDot 2s infinite',
                        }} />
                        {loadingStatus ? 'Yükleniyor...' : isSystemActive ? 'Sistem Aktif' : 'Sistem Kapalı'}
                    </div>
                </div>
            </div>

            {/* ── Stat kartları ────────────────────────────────── */}
            <div className="anim-2" style={{
                display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24,
            }}>
                {statCards.map((s, i) => (
                    <div key={i} className="dash-stat" style={{
                        background: '#111318', border: `1px solid ${s.border}`,
                        borderRadius: 18, padding: '20px 22px',
                        position: 'relative', overflow: 'hidden',
                        transition: 'transform .2s, box-shadow .2s', cursor: 'default',
                    }}>
                        <div style={{
                            position: 'absolute', top: 0, left: '15%', right: '15%', height: 2,
                            background: `linear-gradient(90deg,transparent,${s.accent},transparent)`,
                        }} />
                        <div style={{ position:'absolute', right:-8, bottom:-10, fontSize:'4.5rem', opacity:.04, color:s.accent, transform:'rotate(-15deg)', pointerEvents:'none' }}>{s.icon}</div>
                        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                            <div style={{
                                width:34, height:34, borderRadius:10, flexShrink:0,
                                background:s.bg, border:`1px solid ${s.border}`,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                color:s.accent, fontSize:'.9rem', boxShadow:`0 0 14px ${s.accent}30`,
                            }}>{s.icon}</div>
                            <span style={{ fontSize:'.7rem', fontWeight:600, color:'rgba(255,255,255,.3)', textTransform:'uppercase', letterSpacing:'.8px' }}>{s.label}</span>
                        </div>
                        <div style={{
                            fontSize: s.small ? '.88rem' : '2rem', fontWeight:800, lineHeight:1,
                            color: s.small ? s.accent : '#fff',
                            letterSpacing: s.small ? '0' : '-1px',
                            textTransform: s.small ? 'uppercase' : 'none',
                        }}>{s.val}</div>
                    </div>
                ))}
            </div>

            {/* ── Ana grid ─────────────────────────────────────── */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:20, alignItems:'start' }}>

                {/* Sol: Son talepler */}
                <div className="anim-3" style={{
                    background:'#111318', border:'1px solid rgba(255,255,255,.06)', borderRadius:20, overflow:'hidden',
                }}>
                    <div style={{
                        padding:'18px 22px', borderBottom:'1px solid rgba(255,255,255,.05)',
                        display:'flex', alignItems:'center', justifyContent:'space-between',
                    }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <div style={{
                                width:30, height:30, borderRadius:8,
                                background:'rgba(242,113,39,.1)', border:'1px solid rgba(242,113,39,.2)',
                                display:'flex', alignItems:'center', justifyContent:'center',
                                color:'#f27127', fontSize:'.8rem',
                            }}><FaHistory /></div>
                            <span style={{ fontWeight:700, fontSize:'.9rem', color:'#f1f5f9' }}>Son Talepler</span>
                        </div>
                        <span style={{
                            fontSize:'.65rem', fontWeight:700, padding:'3px 10px', borderRadius:20,
                            background:'rgba(242,113,39,.1)', color:'#f27127',
                            border:'1px solid rgba(242,113,39,.2)', textTransform:'uppercase', letterSpacing:'.5px',
                        }}>Son 8</span>
                    </div>

                    <div style={{ padding:'10px 14px 14px' }}>
                        {loading ? Array.from({length:5}).map((_,i) => (
                            <div key={i} style={{ height:48, borderRadius:12, marginBottom:8, background:'rgba(255,255,255,.03)' }} />
                        )) : recent.map((req) => {
                            const stMap = {
                                approved: { label:'Onay',     color:'#10b981', bg:'rgba(16,185,129,.1)',  border:'rgba(16,185,129,.2)'  },
                                rejected: { label:'Red',      color:'#ef4444', bg:'rgba(239,68,68,.1)',   border:'rgba(239,68,68,.2)'   },
                                pending:  { label:'Bekliyor', color:'#3b82f6', bg:'rgba(59,130,246,.1)',  border:'rgba(59,130,246,.2)'  },
                            };
                            const st    = stMap[req.status] || stMap.pending;
                            const flash = flashRows[req._id];
                            return (
                                <div key={req._id} className={`dash-row${flash ? ' flash-row' : ''}`} style={{
                                    display:'flex', alignItems:'center', gap:10,
                                    padding:'10px 12px', borderRadius:12, marginBottom:6,
                                    background: flash ? `${st.color}10` : 'rgba(255,255,255,.02)',
                                    border:`1px solid ${flash ? st.color+'40' : 'rgba(255,255,255,.04)'}`,
                                    transition:'all .15s', cursor:'default',
                                    position:'relative', overflow:'hidden',
                                }}>
                                    <div style={{ position:'absolute', left:0, top:'18%', bottom:'18%', width:3, borderRadius:'0 3px 3px 0', background:st.color, boxShadow:`0 0 8px ${st.color}80` }} />

                                    <div onClick={() => handleCopy(req.username)} style={{ width:100, flexShrink:0, cursor:'pointer' }}>
                                        <span style={{ fontSize:'.82rem', fontWeight:700, color:'#fff' }}>{req.username}</span>
                                    </div>

                                    <div style={{ width:78, flexShrink:0 }}>
                                        <span style={{
                                            padding:'3px 9px', borderRadius:7,
                                            fontSize:'.62rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px',
                                            background:st.bg, color:st.color, border:`1px solid ${st.border}`,
                                        }}>{st.label}</span>
                                    </div>

                                    <div style={{ flex:1, minWidth:0 }}>
                                        <span style={{ fontSize:'.78rem', fontWeight:500, color:'#cbd5e1', display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                            {bonusMap[req.bonusType] || req.bonusType}
                                        </span>
                                    </div>

                                    {(req.rejectReason || req.adminNote) && (
                                        <div style={{ width:160, flexShrink:0 }}>
                                            <span onClick={() => handleCopy(req.rejectReason || req.adminNote)} title={req.rejectReason || req.adminNote} style={{
                                                display:'block', padding:'3px 9px', borderRadius:7,
                                                background:'rgba(0,0,0,.3)', border:'1px solid rgba(255,255,255,.06)',
                                                fontSize:'.68rem', color:'#94a3b8',
                                                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                                                cursor:'pointer', textAlign:'center', transition:'all .15s',
                                            }}>{req.rejectReason || req.adminNote}</span>
                                        </div>
                                    )}

                                    <div style={{ width:44, flexShrink:0, textAlign:'right', fontSize:'.7rem', color:'rgba(255,255,255,.22)', fontVariantNumeric:'tabular-nums' }}>
                                        {new Date(req.updatedAt).toLocaleTimeString('tr-TR',{hour:'2-digit',minute:'2-digit'})}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Sağ: Operatör dağılımı */}
                <div className="anim-4" style={{
                    background:'#111318', border:'1px solid rgba(255,255,255,.06)', borderRadius:20, overflow:'hidden',
                }}>
                    <div style={{
                        padding:'18px 22px', borderBottom:'1px solid rgba(255,255,255,.05)',
                        display:'flex', alignItems:'center', gap:10,
                    }}>
                        <div style={{
                            width:30, height:30, borderRadius:8,
                            background:'rgba(99,102,241,.1)', border:'1px solid rgba(99,102,241,.2)',
                            display:'flex', alignItems:'center', justifyContent:'center',
                            color:'#818cf8', fontSize:'.8rem',
                        }}><FaChartBar /></div>
                        <span style={{ fontWeight:700, fontSize:'.9rem', color:'#f1f5f9' }}>Operatör Dağılımı</span>
                    </div>

                    <div style={{ padding:'12px 16px 16px' }}>
                        {sortedAssignments.slice(0,8).map((op,i) => {
                            const pct    = totalAssigned > 0 ? (op.count/totalAssigned)*100 : 0;
                            const colors = ['#f27127','#3b82f6','#10b981','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#84cc16'];
                            const col    = colors[i % colors.length];
                            return (
                                <div key={i} className="dash-op" style={{
                                    padding:'12px 14px', borderRadius:13, marginBottom:8,
                                    background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.04)',
                                    transition:'background .15s',
                                }}>
                                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:9 }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                                            <div style={{
                                                width:28, height:28, borderRadius:8, flexShrink:0,
                                                background:`${col}18`, border:`1px solid ${col}30`,
                                                display:'flex', alignItems:'center', justifyContent:'center',
                                                color:col, fontWeight:800, fontSize:'.65rem',
                                            }}>{op.name.slice(0,2).toUpperCase()}</div>
                                            <div>
                                                <div style={{ fontSize:'.8rem', fontWeight:700, color:'#e2e8f0', lineHeight:1.2 }}>{op.name}</div>
                                                <div style={{ fontSize:'.62rem', color:'rgba(255,255,255,.25)', marginTop:1 }}>{op.count} talep</div>
                                            </div>
                                        </div>
                                        <span style={{ fontSize:'.78rem', fontWeight:800, color:col }}>%{pct.toFixed(0)}</span>
                                    </div>
                                    <div style={{ height:4, borderRadius:4, background:'rgba(255,255,255,.06)', overflow:'hidden' }}>
                                        <div style={{
                                            height:'100%', borderRadius:4, width:`${pct}%`,
                                            background:`linear-gradient(90deg,${col}99,${col})`,
                                            transition:'width 1s ease', boxShadow:`0 0 8px ${col}60`,
                                        }} />
                                    </div>
                                </div>
                            );
                        })}
                        {sortedAssignments.length === 0 && (
                            <div style={{ textAlign:'center', padding:'30px 0', color:'rgba(255,255,255,.18)', fontSize:'.8rem' }}>Henüz veri yok</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
